import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowDown, Tent, LayoutGrid, Globe, Zap,
  ShieldCheck, Wrench, Award,
} from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import { heroImage, atmosphereImage, structures, statKeys, portfolio, featureKeys } from '../mock/data';
import { useLang } from '../i18n/LanguageContext';
import { Reveal } from '../hooks/useReveal';

const statIcon = (i) => {
  const cls = 'w-6 h-6 text-gold';
  switch (i) {
    case 'tent': return <Tent className={cls} strokeWidth={1.2} />;
    case 'grid': return <LayoutGrid className={cls} strokeWidth={1.2} />;
    case 'globe': return <Globe className={cls} strokeWidth={1.2} />;
    case 'zap': return <Zap className={cls} strokeWidth={1.2} />;
    default: return null;
  }
};

const featureIcon = (i) => {
  const cls = 'w-5 h-5 text-gold';
  switch (i) {
    case 'shield': return <ShieldCheck className={cls} strokeWidth={1.3} />;
    case 'wrench': return <Wrench className={cls} strokeWidth={1.3} />;
    case 'award': return <Award className={cls} strokeWidth={1.3} />;
    default: return null;
  }
};

// Parallax hero image driven by scroll
function useParallax(ref, speed = 0.35) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      el.style.setProperty('--parallax', `${Math.min(y * speed, 400)}px`);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref, speed]);
}

export default function Home() {
  const { t } = useLang();
  const heroImgRef = useRef(null);
  useParallax(heroImgRef, 0.35);

  const splitTitle = (text) => {
    const parts = text.split(' ');
    if (parts.length < 2) return [text, ''];
    const half = Math.ceil(parts.length / 2);
    return [parts.slice(0, half).join(' '), parts.slice(half).join(' ')];
  };
  const [titleA, titleB] = splitTitle(t('home.structuresTitle'));

  return (
    <main>
      {/* HERO with parallax */}
      <section className="relative h-screen min-h-[680px] w-full overflow-hidden">
        <img
          ref={heroImgRef}
          src={heroImage}
          alt="Luxury event tent at golden hour"
          className="parallax-hero absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" />

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-between pt-32 pb-12">
          <div>
            <h1 className="font-serif-display text-cream text-[110px] md:text-[160px] lg:text-[200px] leading-[0.9] hero-anim">
              <span className="mask-reveal is-visible"><span>TL</span></span>
            </h1>
          </div>
          <div className="flex justify-center">
            <p className="text-cream/90 text-[11px] md:text-xs tracking-luxury uppercase hero-anim hero-anim-delay-3">
              {t('home.heroSubtitle')}
            </p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="hero-anim hero-anim-delay-1">
              <h2 className="font-serif-display text-cream text-[64px] md:text-[100px] lg:text-[140px] leading-[0.9]">
                <span className="mask-reveal is-visible"><span>Event Group</span></span>
              </h2>
            </div>
            <div className="flex items-center gap-6 hero-anim hero-anim-delay-4">
              <Link
                to="/contact"
                className="text-[11px] tracking-luxury uppercase font-medium text-dark bg-cream px-8 py-4 hover:bg-[#e9e2d2] transition-colors duration-300"
              >
                {t('cta.quote')}
              </Link>
              <a
                href="#collectie"
                className="text-[11px] tracking-luxury uppercase font-medium text-cream flex items-center gap-2 hover:text-gold-soft transition-colors"
              >
                {t('cta.discover')} <ArrowDown size={14} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STRUCTURES */}
      <section id="collectie" className="py-24 lg:py-32 bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal variant="up" className="flex items-end justify-between mb-12 lg:mb-16 gap-6">
            <div>
              <div data-stagger="0"><SectionLabel>{t('common.collection')}</SectionLabel></div>
              <h2
                data-stagger="1"
                className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-dark max-w-3xl"
              >
                {titleA}<br />{titleB}
              </h2>
            </div>
            <Link
              data-stagger="2"
              to="/structures"
              className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-gold hover:text-dark transition-colors group"
            >
              {t('cta.viewAll')}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <Reveal variant="fade" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {structures.map((s, i) => (
              <Link
                key={s.id}
                to="/structures"
                data-stagger={i}
                className="group block"
              >
                <div className="cinematic img-hover-zoom aspect-[3/4] mb-5 bg-cream-soft relative">
                  <img src={s.image} alt={t(`tents.${s.id}.name`)} className="w-full h-full object-cover" />
                  {s.comingSoon && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                      <div className="bg-cream/90 backdrop-blur-sm border border-warm px-6 py-3 md:px-8 md:py-4 shadow-lg -rotate-[20deg] w-[130%] text-center">
                        <span className="text-xl md:text-2xl lg:text-3xl tracking-luxury uppercase text-dark font-semibold whitespace-nowrap">
                          {t('common.comingSoon')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] tracking-luxury uppercase text-gold mb-2">{t(`tents.${s.id}.tagline`)}</p>
                <h3 className="font-serif-display text-2xl text-dark mb-4 group-hover:text-gold transition-colors">
                  {t(`tents.${s.id}.name`)}
                </h3>
                <dl className="space-y-1.5 text-xs">
                  <div className="flex justify-between border-b border-warm pb-1.5">
                    <dt className="text-muted-warm">{t('specs.capacity')}</dt>
                    <dd className="text-dark">{s.capacity}</dd>
                  </div>
                  <div className="flex justify-between border-b border-warm pb-1.5">
                    <dt className="text-muted-warm">{t('specs.material')}</dt>
                    <dd className="text-dark">{s.material}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-warm">{t('specs.setup')}</dt>
                    <dd className="text-dark">{t(`tents.${s.id}.setup`)}</dd>
                  </div>
                </dl>
              </Link>
            ))}
          </Reveal>

          <Reveal variant="up" className="mt-14 text-center">
            <Link
              to="/structures"
              className="inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-dark border border-dark px-8 py-4 hover:bg-dark hover:text-cream transition-colors duration-300"
            >
              {t('cta.viewAllStructures')}
              <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 lg:py-28 bg-cream-soft border-y border-warm">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal variant="fade" className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {statKeys.map((s, i) => {
              const value = s.value || t(`stats.${s.key}.value`);
              const label = t(`stats.${s.key}.label`);
              const sub = t(`stats.${s.key}.sub`);
              return (
                <div key={s.key} data-stagger={i} className="flex flex-col">
                  <div className="mb-6">{statIcon(s.icon)}</div>
                  <p className={`font-serif-display text-dark leading-none mb-3 ${
                    String(value).length > 5 ? 'text-3xl lg:text-4xl' : 'text-5xl lg:text-6xl'
                  }`}>{value}</p>
                  <p className="text-sm font-medium text-dark">{label}</p>
                  <p className="text-xs text-muted-warm mt-1">{sub}</p>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* INSPIRATIE */}
      <section className="py-20 lg:py-24 bg-dark text-cream">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal variant="up" className="mb-10 lg:mb-12">
            <div data-stagger="0"><SectionLabel dark>{t('common.inspiration')}</SectionLabel></div>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <h2 data-stagger="1" className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] mb-3">
                  {t('home.inspirationTitle')}
                </h2>
                <p data-stagger="2" className="text-sm text-cream/60 font-light max-w-md">
                  {t('home.inspirationSub')}
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal variant="fade" className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {portfolio.slice(0, 3).map((p, i) => (
              <div key={p.id} data-stagger={i} className="cinematic group relative block aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal variant="left">
            <div data-stagger="0"><SectionLabel>{t('nav.about')}</SectionLabel></div>
            <h2
              data-stagger="1"
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-dark mb-8"
            >
              {t('home.aboutTitle')}
            </h2>
            <p data-stagger="2" className="text-[15px] leading-relaxed text-muted-warm mb-5 font-light">{t('home.aboutP1')}</p>
            <p data-stagger="3" className="text-[15px] leading-relaxed text-muted-warm mb-10 font-light">{t('home.aboutP2')}</p>

            <div className="space-y-6 mb-10">
              {featureKeys.map((f, i) => (
                <div key={f.key} data-stagger={i + 3} className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 border border-warm flex items-center justify-center">
                    {featureIcon(f.icon)}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-dark mb-1">{t(`features.${f.key}.title`)}</h4>
                    <p className="text-sm text-muted-warm leading-relaxed font-light">{t(`features.${f.key}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/about" className="inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-gold hover:text-dark transition-colors group">
              {t('cta.learnMore')}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <Reveal variant="right" className="cinematic img-hover-zoom aspect-[4/5] lg:aspect-[3/4]">
            <img src={atmosphereImage} alt="Luxury tent interior" className="w-full h-full object-cover" />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
